import { makeAutoObservable, runInAction } from 'mobx';
import { IOption } from '../../../components/inputs/select-dropdown/select-dropdown';
import EmailContentService, { EMAIL_CATEGORY, ISupportedIntegration } from '../../../services/api/email-content-service';
import { rootContainer, TYPE } from '../../../services/container/root';
import NotificationEventStore from './notification-event-store';
import { EmailCategoryStore } from './email-category-store';
import PortalService from '../../../services/api/user-service';
import { FEATURES } from '../../../models/Plan';

class EmailContentListStore {
	private _categories: EmailCategoryStore[] = [];
	private _channels: IOption[] = [];
	private _isLoading: boolean = false;
	private _integrations: ISupportedIntegration[] = [];
	private _integrationServiceName = '';
	private _allowedEmailTemplateFeature = false;
	private _allowedEmailReturnTemplateFeature = false;
	private _showTranslationButtons: (show:boolean) => void = (boolean)=> {};
	private _selectedEventId: string | undefined;

	private get emailContentService(): EmailContentService {
		return rootContainer.get<EmailContentService>(TYPE.EmailContentService);
	}

	private get portalService(): PortalService {
		return rootContainer.get<PortalService>(TYPE.UserService);
	}

	public get selectedEvent(): NotificationEventStore | undefined {
		return this._categories
			.find((category) => category.list.find((event) => event.eventId === this._selectedEventId))
			?.list.find((event) => event.eventId === this._selectedEventId);
	}

	public setSelectedEventId(eventId: string | undefined) {
		runInAction(() => {
			this._selectedEventId = eventId;
		});
	}

	async load() {
		this._isLoading = true;
		try {
			const [emailsContent, feature] = await Promise.all([
				this.emailContentService.getNotificationEventsList(),
				this.portalService.getUserFeatures([
					FEATURES.BETA_EMAIL_TEMPLATE_EDITOR,
					FEATURES.BETA_EMAIL_RETURNS_TEMPLATE_EDITOR,
				]),
			]);

			runInAction(() => {
				this._isLoading = false;
				this._integrations = emailsContent.integrations;
				this._integrationServiceName = this._integrations
					.map((integration) => integration.serviceName)
					.join('/ ');
				this._allowedEmailTemplateFeature = feature[0]?.is_allowed;
				this._allowedEmailReturnTemplateFeature = feature[1]?.is_allowed;

				this._categories = emailsContent.categories.map((model) => {
					const isEditEmailAllowed =
						model.category === EMAIL_CATEGORY.returns_email
							? this._allowedEmailReturnTemplateFeature
							: this._allowedEmailTemplateFeature;
					return new EmailCategoryStore(
						model.category,
						model.list.map((e) => new NotificationEventStore(e, isEditEmailAllowed))
					);
				});
				this._channels = emailsContent.channels;
			});
			this._isLoading = false;
		} catch (error) {
			runInAction(() => (this._isLoading = false));
		}
	}

	public get isLoading(): boolean {
		return this._isLoading;
	}

	public get emailCategoryList(): EmailCategoryStore[] {
		return this._categories;
	}

	public get channels(): IOption[] {
		return this._channels;
	}

	public get hasNotificationIntegration(): boolean {
		const count = this._integrations.reduce((acc, integration) => acc + (integration.isActive ? 1 : 0), 0);
		return count > 0;
	}

	public onShowTranslationButton(show: boolean) {
		return this._showTranslationButtons(show);
	}

	public setIsShowTranslationButtons(onShow: (show: boolean) => void) {
		this._showTranslationButtons = onShow;
	}

	public get integrationServiceName(): string {
		return this._integrationServiceName;
	}
	constructor() {
		makeAutoObservable(this);
	}
}

export default new EmailContentListStore();
